import type { MessengerPreChatForm, MessengerSettings, MessengerSettingsResponse } from '@widgets/messenger/types'
import messengerWidgetCastService from '@widgets/messenger/services/messengerWidgetCastService'
import { ApiService } from '@widgets/shared/services/apiService'
import type {
  MessengerChatSurveyRequest,
  MessengerEventHistoryResponse,
  MessengerGetChatResponse,
  MessengerGetMessagesResponse,
  MessengerPreChatResponse,
  MessengerSendEmailRequest,
  MessengerSendMessageRequest,
  MessengerSendMessageResponse,
  MessengerSendSmsRequest,
  MessengerUploadAttachmentRequest,
  MessengerUploadAttachmentResponse,
} from '@widgets/messenger/types/api/rest'
import type { AxiosError } from 'axios'
import sentryMonitoringService from '@widgets/shared/services/monitoring/sentryMonitoringService'
import { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'
import type { CentrifugeTokensResponse } from '@/services/domain/accounts/types'
import type { OnUploadProgress } from '@/services/transport/types'

export type MessengerApiServiceInitParams = {
  requestUrl: string
  isPreview: boolean
}

const widgetEndpoint = '/api/webwidgets'

export class MessengerApiService extends ApiService {
  protected requestUrl: string

  public init({ requestUrl, isPreview }: MessengerApiServiceInitParams) {
    this.requestUrl = requestUrl
    this.registerRequestInterceptor((config) => {
      config.headers['Request-Url'] = requestUrl
      if (isPreview) {
        config.headers['Is-Preview'] = isPreview
        config.withCredentials = true
      }
      return config
    })
  }

  public async getSettings(uniqueCode: string): Promise<MessengerSettings> {
    const path = `${widgetEndpoint}/${uniqueCode}`
    const { data } = await this.get<MessengerSettingsResponse>(path)

    sentryMonitoringService.setExtras({
      messengerSettings: data,
    })

    if (!data || !data.channels) {
      throw new Error('Wrong messenger settings from API')
    }

    return {
      appearance: messengerWidgetCastService.parseAppearance(data.appearance),
      channels: {
        [WebWidgetChannelType.EMAIL]: messengerWidgetCastService.parseEmailChannel(data.channels.email),
        [WebWidgetChannelType.SMS]: messengerWidgetCastService.parseSmsChannel(data.channels.sms),
        [WebWidgetChannelType.CALL]: messengerWidgetCastService.parseCallChannel(data.channels.call),
        [WebWidgetChannelType.CHAT]: messengerWidgetCastService.parseChatChannel(data.channels.chat),
        [WebWidgetChannelType.WHATSAPP]: messengerWidgetCastService.parseWhatsappChannel(data.channels.whatsapp),
        [WebWidgetChannelType.FACEBOOK]: messengerWidgetCastService.parseFacebookChannel(data.channels.facebook),
        [WebWidgetChannelType.INSTAGRAM]: messengerWidgetCastService.parseInstagramChannel(data.channels.instagram),
      },
      channelSorting: messengerWidgetCastService.parseChannelSorting(data.channelSorting),
      uniqueCode,
      userCountry: data.userCountry,
      logoUrl: data.logo?.url || '',
    } satisfies MessengerSettings
  }

  public async getChat(chatToken: string) {
    const path = `${widgetEndpoint}/${chatToken}/chats`
    return this.get<MessengerGetChatResponse>(path)
  }

  public async getMessages(chatToken: string, lastId?: number) {
    const path = `${widgetEndpoint}/chat/messages/${chatToken}`
    return this.get<MessengerGetMessagesResponse>(path, {
      params: {
        perPage: 100,
        lastId,
      },
    })
  }

  public async sendEmail(uniqueCode: string, payload: MessengerSendEmailRequest) {
    const path = `${widgetEndpoint}/${uniqueCode}/channels/email`
    await this.post(path, payload)
  }

  public async sendSms(uniqueCode: string, payload: MessengerSendSmsRequest) {
    const path = `${widgetEndpoint}/${uniqueCode}/channels/sms`
    await this.post(path, payload)
  }

  public async sendPreChat(uniqueCode: string, preChatForm: MessengerPreChatForm) {
    const path = `${widgetEndpoint}/${uniqueCode}/chats`
    return this.post<MessengerPreChatResponse>(path, {
      ...preChatForm,
      pageUrl: this.requestUrl,
    })
  }

  public async getChatTokens(uniqueCode: string) {
    const path = `${widgetEndpoint}/chat/tokens/${uniqueCode}`
    return this.get<CentrifugeTokensResponse>(path)
  }

  public async sendMessage(chatToken: string, form: MessengerSendMessageRequest) {
    const path = `${widgetEndpoint}/${chatToken}/channels/chat`
    return this.post<MessengerSendMessageResponse>(path, form)
  }

  public async endChat(chatToken: string) {
    const path = `${widgetEndpoint}/${chatToken}/chats/close`
    return this.post(path)
  }

  public async sendSurveyResult(chatToken: string, data: MessengerChatSurveyRequest) {
    const path = `${widgetEndpoint}/${chatToken}/chats/survey-result`
    return this.post(path, data)
  }

  public uploadChatAttachment(
    chatToken: string,
    data: MessengerUploadAttachmentRequest,
    onUploadProgress: OnUploadProgress,
  ) {
    const path = `${widgetEndpoint}/${chatToken}/channels/chat/attachment`

    const formData = new FormData()
    formData.append('file', data.file)

    return this.makeCancelableRequest<MessengerUploadAttachmentResponse>((cancelToken) => {
      return this.post<MessengerUploadAttachmentResponse>(path, formData, {
        onUploadProgress,
        cancelToken,
      })
    })
  }

  public uploadEmailAttachment(
    uniqueCode: string,
    data: MessengerUploadAttachmentRequest,
    onUploadProgress: OnUploadProgress,
  ) {
    const path = `${widgetEndpoint}/${uniqueCode}/channels/email/attachment`

    const formData = new FormData()
    formData.append('file', data.file)

    return this.makeCancelableRequest<MessengerUploadAttachmentResponse>((cancelToken) => {
      return this.post<MessengerUploadAttachmentResponse>(path, formData, {
        onUploadProgress,
        cancelToken,
      })
    })
  }

  public isChatBlockedError(e: unknown): e is AxiosError {
    return this.isForbidenError(e) && e.response?.data?.errorCode === 'live_chat.contact_is_blocked'
  }

  public async getEventHistory(uniqueCode: string) {
    const path = `${widgetEndpoint}/${uniqueCode}/ws-events-history`
    const { data } = await this.get<MessengerEventHistoryResponse>(path)
    return data?.items
  }
}

export default new MessengerApiService()
